/**
 * 项目「一键脱敏」弹窗（ADM-LP-S010）：单个项目卡片 / 列表行与勾选项目批量共用。
 * 每个项目一行列出计划脱敏的章数，章节列表默认折叠、点击展开；确认后逐项目沿用内容执行端点并显式传章节，
 * 只纳入已加入投放、AI 脱敏未处理或失败，且本章与相邻投放章原章都可用的章节；处理中、已成功、待重新脱敏，
 * 以及原章或相邻投放章原章失效的不纳入。
 */
import { isQuickDesensitizeStatus, quickDesensitizableChapters } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type ProjectChapter,
  type ProjectSummary,
  landingProjectQueryKey,
  useLandingProjectsChapterLists,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';

/** 可一键脱敏的章节：与服务端 chapters.desensitizable 同一判定（shared quickDesensitizableChapters）。 */
export function quickDesensitizeChapters(chapters: ProjectChapter[]) {
  return quickDesensitizableChapters(chapters.filter((chapter) => chapter.selected));
}

/**
 * 按最新章节说明计划：未处理 / 失败却因本章或相邻投放章原章失效而不纳入的章数（服务端会整批拒收），
 * 以及没有可计划章节时的具体原因。
 */
function describeChapters(chapters: ProjectChapter[], planned: ProjectChapter[]) {
  const selected = chapters.filter((chapter) => chapter.selected);
  const excluded =
    selected.filter((chapter) => isQuickDesensitizeStatus(chapter.desensitization_status)).length -
    planned.length;
  const emptyReason = !selected.length
    ? '尚未加入投放章节'
    : selected.every((chapter) =>
          ['SUCCEEDED', 'UNCHANGED'].includes(chapter.desensitization_status),
        )
      ? '已加入投放的章节均已完成 AI 脱敏'
      : excluded > 0
        ? '未开始或失败的章节原章或相邻投放章已删除或未发布，请先在项目内更新章节选择'
        : '没有未开始或失败的章节，其余章节处理中或待重新脱敏';
  return { excluded, emptyReason };
}

export function canQuickDesensitizeRole(role: string | undefined) {
  return role === 'root' || role === 'admin' || role === 'operator';
}

/** 不读章节即可确定的不可用原因：已归档、书籍不可处理或未加入投放章节；批量入口与弹窗只按这些判定，其余读最新章节。 */
export function quickDesensitizeProjectBlockedReason(project: ProjectSummary) {
  if (project.status === 'ARCHIVED') return '已归档项目不可处理';
  if (!project.can_generate) return project.generation_blocked_reason ?? '当前书籍不可处理';
  if (project.chapters.selected === 0) return '尚未加入投放章节';
  return null;
}

/**
 * 卡片 / 列表行单项目按钮的不可用原因：在项目层原因之外按列表统计判「无可处理章节」（AE §11.1.2）。
 * 可处理章数（chapters.desensitizable）缺省的旧响应只按「已全部完成」判定，不推断其余情况。
 * 批量入口不按统计预判：勾选可能是跨页的旧快照，由弹窗读最新章节再判定。
 */
export function quickDesensitizeBlockedReason(project: ProjectSummary) {
  const blocked = quickDesensitizeProjectBlockedReason(project);
  if (blocked) return blocked;
  const { selected, desensitized, desensitizable } = project.chapters;
  const finished = desensitized !== undefined && desensitized >= selected;
  if (desensitizable === undefined ? finished : desensitizable === 0)
    return finished
      ? '已加入投放的章节均已完成 AI 脱敏'
      : '没有可一键脱敏的章节：其余章节处理中、待重新脱敏，或本章 / 相邻投放章原章已删除或未发布';
  return null;
}

export function LandingProjectQuickDesensitizeDialog({
  projects,
  onClose,
  onSubmitted,
}: {
  projects: ProjectSummary[];
  onClose: () => void;
  /** 提交成功的项目 id，供批量勾选移出已提交项。 */
  onSubmitted?: (projectIds: string[]) => void;
}) {
  const client = useQueryClient();
  const loadable = projects.filter(
    (project) => quickDesensitizeProjectBlockedReason(project) === null,
  );
  const queries = useLandingProjectsChapterLists(loadable.map((project) => project.id));
  const [expanded, setExpanded] = useState(new Set<string>());
  const plans = projects.map((project) => {
    const blocked = quickDesensitizeProjectBlockedReason(project);
    const query = blocked ? undefined : queries[loadable.indexOf(project)];
    const chapters = query?.data ? quickDesensitizeChapters(query.data) : [];
    const { excluded, emptyReason } = query?.data
      ? describeChapters(query.data, chapters)
      : { excluded: 0, emptyReason: '' };
    // 与项目详情共用缓存：有缓存时先渲染旧数据并在后台刷新（离线时刷新暂停），拿到本次最新数据前不按旧计划提交；
    // 读取失败的项目不提交。
    const reading = Boolean(query && (query.isPending || query.fetchStatus !== 'idle'));
    return { project, blocked, query, chapters, excluded, emptyReason, reading };
  });
  const loading = plans.some((plan) => plan.reading);
  const ready = plans.filter(
    (plan) => plan.chapters.length > 0 && !plan.reading && !plan.query?.isError,
  );
  const total = ready.reduce((sum, plan) => sum + plan.chapters.length, 0);
  const submit = useMutation({
    mutationFn: async () => {
      // 无批量端点：逐项目提交，各自落审计与幂等键；失败项目不影响已成功的。
      const results = await Promise.allSettled(
        ready.map((plan) =>
          landingProjectsApi.process(plan.project.id, {
            operation: 'desensitize',
            chapter_ids: plan.chapters.map((chapter) => chapter.id),
          }),
        ),
      );
      return ready.map((plan, index) => ({ plan, result: results[index] }));
    },
    onSuccess: (outcomes) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      const succeeded = outcomes.filter((item) => item.result?.status === 'fulfilled');
      const failed = outcomes.filter((item) => item.result?.status !== 'fulfilled');
      const chapters = succeeded.reduce((sum, item) => sum + item.plan.chapters.length, 0);
      onSubmitted?.(succeeded.map((item) => item.plan.project.id));
      if (failed.length) {
        const reasons = failed
          .slice(0, 3)
          .map((item) => {
            const reason =
              item.result?.status === 'rejected' && item.result.reason instanceof Error
                ? item.result.reason.message
                : '提交失败';
            return `${item.plan.project.name}：${reason}`;
          })
          .join('；');
        toast(
          `已提交 ${succeeded.length} 个项目共 ${chapters} 章，${failed.length} 个项目失败（${reasons}）`,
          { tone: succeeded.length ? 'warn' : 'neg' },
        );
      } else toast(`已提交 ${succeeded.length} 个项目共 ${chapters} 章 AI 脱敏`, { tone: 'pos' });
      onClose();
    },
    onError: (error: Error) => toast(`提交 AI 脱敏失败：${error.message}`, { tone: 'neg' }),
  });
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Dialog
      title="一键脱敏"
      subtitle={`${projects.length} 个项目 · 只处理已加入投放、AI 脱敏未开始或失败的章节`}
      icon="IconShield"
      // 提交中不关闭：mutation 回调会在完成后关窗，期间若重开了别的一键脱敏弹窗会被误关。
      onClose={() => {
        if (!submit.isPending) onClose();
      }}
      width={720}
      footer={
        <>
          <button type="button" className="btn" disabled={submit.isPending} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={loading || total === 0 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending
              ? '提交中…'
              : total
                ? `开始脱敏（${ready.length} 个项目 · ${total} 章）`
                : '开始脱敏'}
          </button>
        </>
      }
    >
      <DialogBody>
        <div className="vstack" style={{ gap: 8 }}>
          {plans.map(({ project, blocked, query, chapters, excluded, emptyReason, reading }) => {
            const failed = chapters.filter(
              (chapter) => chapter.desensitization_status === 'FAILED',
            );
            const open = expanded.has(project.id);
            return (
              <section
                key={project.id}
                className="card-flat"
                aria-label={`项目 ${project.name}`}
                style={{ padding: 10 }}
              >
                <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, overflowWrap: 'anywhere', minWidth: 0 }}>
                    {project.name}
                  </span>
                  <span className="mono muted">{project.code}</span>
                  <div style={{ flex: 1 }} />
                  {blocked ? (
                    <span className="muted">{blocked}</span>
                  ) : reading ? (
                    <span className="muted">读取章节…</span>
                  ) : query?.isError ? (
                    <span style={{ color: 'var(--neg)' }}>读取章节失败：{query.error.message}</span>
                  ) : chapters.length === 0 ? (
                    <span className="muted">{emptyReason}</span>
                  ) : (
                    <>
                      <span className="mono">
                        计划 {chapters.length} 章（未开始 {chapters.length - failed.length} · 失败{' '}
                        {failed.length}）
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        aria-expanded={open}
                        onClick={() => toggle(project.id)}
                      >
                        {open ? '收起章节' : '查看章节'}
                      </button>
                    </>
                  )}
                </div>
                {!blocked && !reading && !query?.isError && chapters.length > 0 && excluded > 0 && (
                  <div className="muted" style={{ marginTop: 4 }}>
                    另有 {excluded}{' '}
                    章因原章或相邻投放章已删除或未发布未纳入，请先在项目内更新章节选择
                  </div>
                )}
                {open && chapters.length > 0 && (
                  <ul className="muted" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {chapters.map((chapter) => (
                      <li key={chapter.id} style={{ overflowWrap: 'anywhere' }}>
                        第 {chapter.chapter_index} 章 · {chapter.title}
                        {chapter.desensitization_status === 'FAILED' ? ' · 失败' : ' · 未开始'}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
          <p className="muted" style={{ margin: 0 }}>
            已成功、处理中、待重新脱敏，以及原章或相邻投放章失效的章节不纳入；提交后会调用 AI
            模型并计费，可在执行队列查看进度。
          </p>
        </div>
      </DialogBody>
    </Dialog>
  );
}
